'use client'

import { FC, Fragment } from 'react'
import { SessionProlongModal } from '../SessionProlongModal'
import { CountryItem, Reservation } from '@/root/shared-types'
import { testIds } from '@/root/shared-consts'
import type { ReservationWithModification } from '@/root/modules/shared/EditReservation/types'
import { EditReservationTabs } from '../EditReservationTabs'
import { EDIT_TABS } from '@/root/modules/shared/EditReservation/consts'
import { EditActions } from '../EditActions'
import { SessionExpiryBanner } from '../SessionExpiryBanner'
import { RoundTripIcon } from '@/root/ui/shared/icons/RoundTripIcon'
import { EditReservationTicketsTab } from '../EditReservationTicketsTab'
import { CartWithBanners } from '../CartWithBanners'
import { EditReservationCustomerTab } from '../EditReservationCustomerTab'
import { Modal } from '@/root/components/shared/Modal/Modal'
import { ToastContainer } from 'react-toastify'
import cn from 'classnames'
import { PaymentSection } from '../PaymentSection'
import { JourneySection } from '../JourneySection'
import { useEditReservationContent } from '@/root/modules/shared/EditReservation/hooks/useEditReservationContent'

interface EditReservationContentProps {
  /**
   * Last committed state of the reservation. Must NOT be derived from `reservation`: on a reload
   * of an open session `start()` returns the staged state, so a self-diff would read as "no
   * changes" and hide both the cart highlight and any new refund offer.
   */
  committedReservation: Reservation
  countries: CountryItem[]
  /** Reservation including the uncommitted changes of the open modification session. */
  reservation: ReservationWithModification
}

export const EditReservationContent: FC<EditReservationContentProps> = ({
  reservation,
  committedReservation,
  countries,
}) => {
  const {
    locale,
    getTranslation,
    reservation: currentReservation,
    reservationToken,
    isModifying,
    navigation,
    session,
    route,
    tickets,
    customer,
    cart,
    finish,
    payment,
  } = useEditReservationContent({ reservation, committedReservation })

  return (
    <div className="flex w-full flex-col gap-6 pt-6">
      {/* Title and Session Expiry */}
      <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-[1fr_320px]">
        <h1 className="text-primary-base text-center text-2xl font-bold">
          {getTranslation('editReservationTitle')} {currentReservation.reservationId}
        </h1>
        {!!session.expireAt && (
          <div className="w-fit justify-self-center lg:w-full">
            <SessionExpiryBanner expireAt={session.expireAt} onExpiringSoon={session.openProlong} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        {/* Left: Tabs (row) + Content */}
        <div className={cn('flex flex-col gap-4', payment.status.isActive && 'pointer-events-none opacity-50')}>
          {/* Tabs in a row */}
          <EditReservationTabs activeTab={navigation.activeTab} onTabChange={navigation.requestTab} />

          <div className="flex flex-col gap-6">
            {navigation.activeTab === EDIT_TABS.ROUTE &&
              route.legs.map((leg) => (
                <Fragment key={leg.direction}>
                  {leg.hasDividerBefore && (
                    <div className="flex items-center gap-2 py-1">
                      <RoundTripIcon width={24} height={24} color="primary-base" />
                      <div className="border-primary-base grow border-t" />
                    </div>
                  )}
                  <JourneySection
                    locale={locale}
                    sailPackage={leg.sailPackage}
                    sailRef={leg.sailRef}
                    selectedDate={leg.edit.date}
                    onDayClick={leg.edit.onDayClick}
                    isDayDisabled={leg.edit.isDayDisabled}
                    time={leg.edit.time}
                    routeInfo={leg.edit.routeInfo}
                    modifiedInventory={leg.edit.modifiedInventory}
                    isInventoryLoading={leg.edit.isInventoryLoading}
                    selectedSailRefId={leg.edit.sailRefId}
                    setRefIdAndTime={leg.edit.setRefIdAndTime}
                    routeTitle={leg.sailRef.routeLegTitle}
                    isSaved={leg.edit.isSaved}
                    getTranslation={getTranslation}
                    requiredPassengers={leg.counts.passengers}
                    requiredResidentPassengers={leg.counts.residentPassengers}
                    requiredVehicles={leg.counts.vehicles}
                    requiredResidentVehicles={leg.counts.residentVehicles}
                    requiredBicycles={leg.counts.bicycles}
                    disabled={isModifying}
                    isSaving={leg.isSaving}
                    testId={testIds.edit.journeySection}
                  />
                </Fragment>
              ))}
            {navigation.activeTab === EDIT_TABS.TICKETS && (
              <>
                {/* Direction tabs */}
                {tickets.showDirectionTabs && (
                  <div className="flex flex-wrap justify-center gap-2 md:justify-start">
                    {navigation.directionTabs.map((dir) => (
                      <button
                        key={dir.id}
                        data-testid={dir.testId}
                        type="button"
                        onClick={() => navigation.requestDirection(dir.id)}
                        className={cn(
                          'flex h-12 items-center justify-center gap-2 rounded-full px-5',
                          'text-base leading-normal font-medium whitespace-nowrap transition-colors duration-300 focus:outline-none',
                          navigation.activeDirection === dir.id ? 'bg-base-medium-light text-white' : 'text-gray-500'
                        )}
                      >
                        <span>{dir.title}</span>
                        {dir.date && <span className="text-sm">{dir.date}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {tickets.legs.map(
                  (leg) =>
                    leg.isShown && <EditReservationTicketsTab key={leg.key} {...leg.props} countries={countries} />
                )}
              </>
            )}
            {navigation.activeTab === EDIT_TABS.CUSTOMER && (
              <EditReservationCustomerTab
                ref={customer.ref}
                reservation={currentReservation}
                onDirtyChange={customer.onDirtyChange}
                onSaved={customer.onSaved}
              />
            )}
          </div>
        </div>

        {/* Right: Cart - Always visible, sticky on desktop */}
        <div className="hidden lg:sticky lg:top-8 lg:flex lg:flex-col lg:gap-3 lg:self-start">
          <CartWithBanners
            currentReservation={currentReservation}
            originalReservation={cart.committedReservation}
            toPayAmount={cart.toPayAmount}
            isRefundOffered={cart.isRefundOffered}
            toPayBannerText={cart.toPayBannerText}
            toRefundBannerText={cart.toRefundBannerText}
            confirmBannerText={getTranslation('editConfirmWithFinish')}
            hasSaved={cart.hasSaved}
            hideFirstLeg={cart.hideFirstLeg}
            hideSecondLeg={cart.hideSecondLeg}
          />
        </div>
      </div>

      {/* Mobile: Cart */}
      <div className="flex flex-col gap-3 lg:hidden">
        <CartWithBanners
          currentReservation={currentReservation}
          originalReservation={cart.committedReservation}
          toPayAmount={cart.toPayAmount}
          isRefundOffered={cart.isRefundOffered}
          toPayBannerText={cart.toPayBannerText}
          toRefundBannerText={cart.toRefundBannerText}
          confirmBannerText={getTranslation('editConfirmWithFinish')}
          hasSaved={cart.hasSaved}
        />
      </div>

      {/* Payment section (inline after commit) */}
      {payment.status.isActive && (
        <PaymentSection
          methods={payment.selection.methods}
          selectedMethod={payment.selection.selectedMethod}
          onSelectMethod={payment.selection.selectMethod}
          isTermsAccepted={payment.selection.isTermsAccepted}
          onToggleTerms={payment.selection.toggleTerms}
          isBusy={payment.status.isProcessing || payment.status.isCancelling}
          canPay={payment.status.canPay}
          isProcessing={payment.status.isProcessing}
          isCancelling={payment.status.isCancelling}
          toPayAmount={cart.toPayAmount}
          termsText={payment.termsText}
          onPay={payment.pay}
          onCancel={payment.actions.cancel}
          creditBalance={payment.selection.creditBalance}
          isCreditBalanceLoading={payment.selection.isCreditBalanceLoading}
          isCreditInsufficient={payment.selection.isCreditInsufficient}
        />
      )}

      {/* Save/Cancel Actions — hidden in payment mode */}
      {!payment.status.isActive && (
        <EditActions
          canFinish={finish.canFinish}
          isLoading={isModifying}
          isFinishing={finish.isFinishing}
          hasExtraPayment={finish.hasExtraPayment}
          onCancel={finish.handleCancel}
          onFinish={finish.handleFinish}
        />
      )}
      <Modal isOpen={navigation.pendingNavigation !== null} onClose={navigation.closeUnsavedModal} hideCloseButton>
        <div className="flex flex-col items-center gap-4">
          <p className="text-neutral-darkest text-center text-lg font-semibold">
            {getTranslation('editUnsavedChangesTitle')}
          </p>
          <p className="text-neutral-darker text-center text-sm">{getTranslation('editUnsavedChangesBody')}</p>
          <div className="flex w-full gap-4 pt-2">
            <button
              data-testid={testIds.edit.unsavedDiscard}
              onClick={navigation.discardAndNavigate}
              className="border-neutral-medium-light text-neutral-darkest flex-1 rounded-lg border px-6 py-3 font-medium transition-colors hover:bg-gray-50"
              type="button"
            >
              {getTranslation('editUnsavedChangesDiscard')}
            </button>
            <button
              data-testid={testIds.edit.unsavedStay}
              onClick={navigation.closeUnsavedModal}
              className="bg-base-medium-light flex-1 rounded-lg px-6 py-3 font-medium text-white transition-colors hover:opacity-90"
              type="button"
            >
              {getTranslation('editUnsavedChangesStay')}
            </button>
          </div>
        </div>
      </Modal>
      {/* Session prolong modal */}
      {reservationToken && (
        <SessionProlongModal
          isOpen={session.isProlongOpen}
          expireAt={session.expireAt ?? ''}
          reservationToken={reservationToken}
          locale={locale}
          onClose={session.closeProlong}
          onSessionExtended={session.onSessionExtended}
          onEndEditing={finish.handleCancel}
          onExpiredRedirect={session.onExpiredRedirect}
          onError={session.onError}
        />
      )}
      <ToastContainer toastClassName="text-neutral-darkest" />
    </div>
  )
}
